/**
 * RPC Provider Manager
 * Handles provider selection, health monitoring, and automatic failover
 */

import * as Sentry from '@sentry/react';
import { TritonProvider } from './providers/TritonProvider';
import { AlchemyProvider } from './providers/AlchemyProvider';
import {
  RPCProvider,
  ProviderId,
  ProviderStatus,
  RPCProviderState,
  RPCProviderConfig,
  FAILOVER_CONFIG,
  RPC_PREFERENCE_KEY,
} from './types';

type StateChangeCallback = (state: RPCProviderState) => void;

export class RPCProviderManager {
  private providers: Map<ProviderId, RPCProvider> = new Map();
  private state: RPCProviderState;
  private healthCheckInterval: ReturnType<typeof setInterval> | null = null;
  private highLatencyStartTime: Map<ProviderId, number> = new Map();
  private stateChangeCallbacks: Set<StateChangeCallback> = new Set();

  constructor(config: RPCProviderConfig) {
    // Initialize providers based on config (priority order: Triton > Alchemy)
    if (config.tritonRpcUrl) {
      this.providers.set('triton', new TritonProvider(config.tritonRpcUrl, config.tritonWsUrl));
    }

    // Alchemy is HTTP-only (no WebSocket support for Solana)
    if (config.alchemyRpcUrl) {
      this.providers.set('alchemy', new AlchemyProvider(config.alchemyRpcUrl));
    }

    // Load user preference from localStorage
    const savedPreference = this.loadUserPreference();

    // Initialize state
    this.state = {
      activeProvider: this.determineInitialProvider(savedPreference),
      providers: this.initializeProviderStatuses(),
      userPreference: savedPreference,
      failoverCooldownUntil: null,
      isFailingOver: false,
    };

  }

  private loadUserPreference(): 'auto' | ProviderId {
    if (typeof window === 'undefined') return 'auto';

    const saved = localStorage.getItem(RPC_PREFERENCE_KEY);
    if (saved === 'triton' || saved === 'alchemy' || saved === 'custom' || saved === 'auto') {
      return saved;
    }
    // Stale 'helius' (or any unrecognized) preferences fall through to 'auto'.
    return 'auto';
  }

  private determineInitialProvider(preference: 'auto' | ProviderId): ProviderId {
    // If user has a specific preference and that provider exists, use it
    if (preference !== 'auto' && this.providers.has(preference)) {
      return preference;
    }

    // Default priority: Triton > Alchemy
    if (this.providers.has('triton')) return 'triton';
    if (this.providers.has('alchemy')) return 'alchemy';

    // Fallback - should not happen in normal use
    const firstProvider = this.providers.keys().next().value;
    return firstProvider || 'triton';
  }

  private initializeProviderStatuses(): Record<ProviderId, ProviderStatus> {
    const statuses: Partial<Record<ProviderId, ProviderStatus>> = {};

    for (const [id, provider] of this.providers) {
      statuses[id] = {
        id,
        name: provider.name,
        healthy: true, // Assume healthy initially
        latency: 0,
        lastCheck: 0,
        consecutiveFailures: 0,
        supportsWebhooks: provider.supportsWebhooks,
      };
    }

    return statuses as Record<ProviderId, ProviderStatus>;
  }

  /**
   * Subscribe to state changes
   */
  onStateChange(callback: StateChangeCallback): () => void {
    this.stateChangeCallbacks.add(callback);
    return () => this.stateChangeCallbacks.delete(callback);
  }

  private notifyStateChange(): void {
    for (const callback of this.stateChangeCallbacks) {
      callback(this.state);
    }
  }

  /**
   * Get current state
   */
  getState(): RPCProviderState {
    return { ...this.state };
  }

  /**
   * Get the active provider instance
   */
  getActiveProvider(): RPCProvider | undefined {
    return this.providers.get(this.state.activeProvider);
  }

  /**
   * Get HTTP endpoint from active provider
   */
  getHttpEndpoint(): string {
    const provider = this.getActiveProvider();
    if (!provider) {
      console.error('[RPCProviderManager] No active provider available');
      return '';
    }
    return provider.getHttpEndpoint();
  }

  /**
   * Get WebSocket endpoint from active provider
   */
  getWsEndpoint(): string | undefined {
    const provider = this.getActiveProvider();
    return provider?.getWsEndpoint();
  }

  /**
   * Set user preference for provider
   */
  setUserPreference(preference: 'auto' | ProviderId): void {
    this.state.userPreference = preference;
    
    if (typeof window !== 'undefined') {
      localStorage.setItem(RPC_PREFERENCE_KEY, preference);
    }

    if (preference === 'auto') {
      // Immediately select the best provider based on latency
      const bestProvider = this.selectBestProvider();
      if (bestProvider) {
        this.switchProvider(bestProvider);
      }
    } else if (this.providers.has(preference)) {
      // Switch to the explicitly selected provider
      this.switchProvider(preference);
    }

    this.notifyStateChange();
  }

  /**
   * Provider priority order: Triton (primary) > Alchemy (fallback)
   */
  private static readonly PROVIDER_PRIORITY: ProviderId[] = ['triton', 'alchemy'];

  /**
   * Select the best provider respecting priority order.
   * Only falls back to lower-priority providers if higher-priority ones are unhealthy
   * or have unacceptably high latency (>1000ms).
   */
  private selectBestProvider(): ProviderId | null {
    const UNACCEPTABLE_LATENCY_MS = 1000;

    // Go through providers in priority order
    for (const providerId of RPCProviderManager.PROVIDER_PRIORITY) {
      if (!this.providers.has(providerId)) continue;

      const status = this.state.providers[providerId];
      if (!status) continue;

      // If healthy and has acceptable latency, use this provider
      if (status.healthy && (status.latency === 0 || status.latency < UNACCEPTABLE_LATENCY_MS)) {
        return providerId;
      }
    }

    // If no provider has acceptable latency, fall back to any healthy provider in priority order
    for (const providerId of RPCProviderManager.PROVIDER_PRIORITY) {
      if (!this.providers.has(providerId)) continue;

      const status = this.state.providers[providerId];
      if (status?.healthy) {
        return providerId;
      }
    }

    return null;
  }

  /**
   * Switch to a specific provider
   */
  switchProvider(providerId: ProviderId): boolean {
    if (!this.providers.has(providerId)) {
      console.warn('[RPCProviderManager] Provider not available:', providerId);
      return false;
    }

    const previousProvider = this.state.activeProvider;
    this.state.activeProvider = providerId;

    // Tag Sentry with current RPC provider for debugging
    Sentry.setTag('rpc_provider', providerId);

    // Log failover events
    if (previousProvider !== providerId) {
      console.info(`[RPCProviderManager] Switched from ${previousProvider} to ${providerId}`);
      Sentry.addBreadcrumb({
        category: 'rpc',
        message: `RPC failover: ${previousProvider} -> ${providerId}`,
        level: 'warning',
      });
    }

    this.notifyStateChange();

    return true;
  }

  /**
   * Start health monitoring
   */
  startHealthMonitoring(): void {
    if (this.healthCheckInterval) return;

    // Initial health check
    this.performHealthChecks();

    // Schedule periodic health checks
    this.healthCheckInterval = setInterval(() => {
      this.performHealthChecks();
    }, FAILOVER_CONFIG.HEALTH_CHECK_INTERVAL_MS);

  }

  /**
   * Stop health monitoring
   */
  stopHealthMonitoring(): void {
    if (this.healthCheckInterval) {
      clearInterval(this.healthCheckInterval);
      this.healthCheckInterval = null;
    }
  }

  /**
   * Perform health checks on all providers
   */
  private async performHealthChecks(): Promise<void> {
    const checks = Array.from(this.providers.entries()).map(async ([id, provider]) => {
      const result = await provider.healthCheck();
      
      const status = this.state.providers[id];
      if (!status) return;

      status.lastCheck = result.timestamp;
      status.latency = result.latency;

      if (result.healthy) {
        status.healthy = true;
        status.consecutiveFailures = 0;
        this.highLatencyStartTime.delete(id);

        // Check for sustained high latency
        if (result.latency > FAILOVER_CONFIG.HIGH_LATENCY_THRESHOLD_MS) {
          if (!this.highLatencyStartTime.has(id)) {
            this.highLatencyStartTime.set(id, Date.now());
          } else {
            const highLatencyDuration = Date.now() - this.highLatencyStartTime.get(id)!;
            if (highLatencyDuration > FAILOVER_CONFIG.HIGH_LATENCY_DURATION_MS) {
              console.warn(`[RPCProviderManager] ${id} has sustained high latency (${result.latency}ms for ${highLatencyDuration}ms)`);
              this.handleProviderIssue(id);
            }
          }
        }
      } else {
        status.consecutiveFailures++;
        console.warn(`[RPCProviderManager] ${id} health check failed (${status.consecutiveFailures} consecutive):`, result.error);

        if (status.consecutiveFailures >= FAILOVER_CONFIG.MAX_CONSECUTIVE_FAILURES) {
          status.healthy = false;
          Sentry.withScope((scope) => {
            scope.setTag('rpc_provider', id);
            scope.setExtra('consecutive_failures', status.consecutiveFailures);
            scope.setExtra('last_error', result.error || 'unknown');
            Sentry.captureMessage(`RPC provider ${id} marked unhealthy after ${status.consecutiveFailures} consecutive failures`, 'warning');
          });
          this.handleProviderIssue(id);
        }
      }
    });

    await Promise.all(checks);

    // In Auto mode, check if we should switch to a higher-priority provider that recovered
    if (this.state.userPreference === 'auto') {
      const bestProvider = this.selectBestProvider();
      if (bestProvider && bestProvider !== this.state.activeProvider) {
        const currentPriority = RPCProviderManager.PROVIDER_PRIORITY.indexOf(this.state.activeProvider);
        const bestPriority = RPCProviderManager.PROVIDER_PRIORITY.indexOf(bestProvider);
        const currentStatus = this.state.providers[this.state.activeProvider];

        // Only auto-switch in these cases:
        // 1. Current provider is unhealthy - switch to best healthy alternative
        // 2. Best provider is higher priority (lower index) - prefer primary providers
        // Never auto-switch from Triton to Alchemy just for latency
        const shouldSwitch =
          !currentStatus?.healthy ||
          (bestPriority < currentPriority);

        if (shouldSwitch) {
          console.info(`[RPCProviderManager] Auto-switching: current=${this.state.activeProvider} (healthy=${currentStatus?.healthy}, priority=${currentPriority}), best=${bestProvider} (priority=${bestPriority})`);
          this.switchProvider(bestProvider);
        }
      }
    }

    this.notifyStateChange();
  }

  /**
   * Handle provider issues and potentially trigger failover
   */
  private handleProviderIssue(providerId: ProviderId): void {
    // Only failover if this is the active provider
    if (this.state.activeProvider !== providerId) return;

    // Respect user preference - don't failover if user explicitly chose this provider
    if (this.state.userPreference === providerId) {
      return;
    }

    // Check cooldown
    if (this.state.failoverCooldownUntil && Date.now() < this.state.failoverCooldownUntil) {
      return;
    }

    // Find a healthy alternative
    const healthyAlternative = this.findHealthyAlternative(providerId);
    if (!healthyAlternative) {
      console.warn('[RPCProviderManager] No healthy alternative provider available');
      return;
    }

    // Perform failover
    this.state.isFailingOver = true;
    this.state.failoverCooldownUntil = Date.now() + FAILOVER_CONFIG.FAILOVER_COOLDOWN_MS;

    this.switchProvider(healthyAlternative);
    
    this.state.isFailingOver = false;
    this.notifyStateChange();
  }

  /**
   * Find a healthy alternative provider, respecting priority order
   */
  private findHealthyAlternative(excludeId: ProviderId): ProviderId | null {
    // Iterate in priority order: Triton > Alchemy
    for (const providerId of RPCProviderManager.PROVIDER_PRIORITY) {
      if (providerId === excludeId) continue;
      if (!this.providers.has(providerId)) continue;

      const status = this.state.providers[providerId];
      if (status?.healthy) {
        return providerId;
      }
    }
    return null;
  }

  /**
   * Get Triton provider for WebSocket keepalive management
   */
  getTritonProvider(): TritonProvider | undefined {
    const provider = this.providers.get('triton');
    return provider instanceof TritonProvider ? provider : undefined;
  }

  /**
   * Cleanup
   */
  destroy(): void {
    this.stopHealthMonitoring();
    this.stateChangeCallbacks.clear();
    
    // Stop Triton WS keepalive if active
    const triton = this.getTritonProvider();
    triton?.stopWsKeepalive();
  }
}
